// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { useState, useEffect, useCallback } from 'react';
import { probeRuntimeHealth } from '@utils/runtimeStatusProbe';

interface OnlineStatus {
  isOnline: boolean;
  wasOffline: boolean;
  checkConnection: () => Promise<boolean>;
}

export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);

  const checkConnection = useCallback(async (): Promise<boolean> => {
    // Skip health check in static preview environments to avoid ERR_ABORTED noise
    if (import.meta.env?.MODE === 'development' && !import.meta.env?.VITE_API_URL) {
      setIsOnline(navigator.onLine);
      return navigator.onLine;
    }

    try {
      // Use AbortController to avoid ERR_ABORTED noise in console
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      // CD-14: route through the operational-status probe authority instead
      // of inlining the /api/health literal + fetch here.
      const { ok } = await probeRuntimeHealth(controller.signal);

      clearTimeout(timeout);
      setIsOnline(ok);
      return ok;
    } catch {
      // Silently fail — this is expected in static preview environments
      setIsOnline(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setWasOffline(!isOnline);
      setIsOnline(true);
      // Verify actual connectivity
      checkConnection();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic connection check
    const interval = setInterval(() => {
      if (navigator.onLine) {
        checkConnection();
      }
    }, 30000); // Check every 30 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [isOnline, checkConnection]);

  return {
    isOnline,
    wasOffline,
    checkConnection,
  };
}


// Import for the queue hook
